import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, Stack, Autocomplete,
  Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { API_URL } from './config';

const ORANGE = '#F07828';
const SURFACE = {
  borderRadius: 2,
  border: '1px solid rgba(25,118,210,.2)',
  bgcolor: '#fff',
};
const LANGUAGES = [
  ['en-IN', 'English (India)'], ['te-IN', 'Telugu'], ['hi-IN', 'Hindi'],
  ['ta-IN', 'Tamil'], ['kn-IN', 'Kannada'], ['ml-IN', 'Malayalam'],
  ['bn-IN', 'Bengali'], ['mr-IN', 'Marathi'], ['ur-IN', 'Urdu'],
];
const configuredRoot = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : API_URL;
const apiRoot = !configuredRoot
  ? '/api'
  : /\/api\/?$/.test(configuredRoot)
    ? configuredRoot.replace(/\/$/, '')
    : `${configuredRoot.replace(/\/$/, '')}/api`;
const api = axios.create({ baseURL: `${apiRoot}/prompt-studio`, timeout: 120000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function errorMessage(error) {
  return error?.response?.data?.error || error?.message || 'Something went wrong.';
}

function safeFilename(value) {
  return String(value || 'PromptStudio').replace(/[^a-z0-9_-]/gi, '-').slice(0, 80);
}

function safeSheetName(value, used) {
  const base = String(value || 'Question').replace(/[\\/?*\[\]:]/g, ' ').slice(0, 28) || 'Question';
  let name = base;
  let suffix = 2;
  while (used.has(name)) name = `${base.slice(0, 25)}-${suffix++}`;
  used.add(name);
  return name;
}

function reportRows(entries) {
  return entries.map((entry, index) => ({
    No: index + 1,
    StudentID: entry.studentId || '',
    StudentName: entry.studentName || '',
    Question: entry.questionText || '',
    OriginalPrompt: entry.originalPrompt || '',
    ImprovedPrompt: entry.improvedPrompt || '',
    Score: entry.score ?? '',
    Language: entry.review?.language || '',
    Explanation: entry.review?.nativeExplanation || '',
    Changes: (entry.review?.changes || []).map((change) =>
      `${change.label}: ${change.explanation}`).join('; '),
    SubmittedAt: entry.createdAt || '',
  }));
}

function groupedQuestions(entries, room) {
  const groups = new Map();
  entries.forEach((entry) => {
    const question = entry.questionText || room?.question || 'Unspecified question';
    if (!groups.has(question)) groups.set(question, []);
    groups.get(question).push(entry);
  });
  return [...groups.entries()];
}

function exportReport(format, room, entries) {
  const filename = safeFilename(`PromptStudio-${room?.id || 'report'}`);
  const groups = groupedQuestions(entries, room);

  if (format === 'excel') {
    const workbook = XLSX.utils.book_new();
    const used = new Set();
    if (!groups.length) groups.push([room?.question || 'Question 1', []]);
    groups.forEach(([question, submissions], index) => {
      const rows = reportRows(submissions);
      const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Question: question }]);
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName(`Question ${index + 1}`, used));
    });
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    return;
  }

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 36;
  const width = pdf.internal.pageSize.getWidth() - margin * 2;
  let firstPage = true;
  (groups.length ? groups : [[room?.question || 'Question 1', []]]).forEach(([question, submissions], index) => {
    if (!firstPage) pdf.addPage();
    firstPage = false;
    let y = 42;
    pdf.setFontSize(15);
    pdf.text(String(room?.title || 'Prompt Studio report'), margin, y);
    y += 26;
    pdf.setFontSize(11);
    const heading = pdf.splitTextToSize(`Question ${index + 1}: ${question}`, width);
    pdf.text(heading, margin, y);
    y += heading.length * 15 + 16;
    pdf.setFontSize(9);
    reportRows(submissions).forEach((row) => {
      const content = [
        `${row.No}. ${row.StudentName} (${row.StudentID}) | Score: ${row.Score}`,
        `Original: ${row.OriginalPrompt}`,
        `Improved: ${row.ImprovedPrompt}`,
        `Feedback: ${row.Explanation}`,
      ].join('\n');
      const lines = pdf.splitTextToSize(content, width);
      if (y + lines.length * 12 > 790) {
        pdf.addPage();
        y = 42;
      }
      pdf.text(lines, margin, y);
      y += lines.length * 12 + 14;
    });
  });
  pdf.save(`${filename}.pdf`);
}

function ReportDialog({ open, onClose, room, entries }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Download Class Report</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Export all submissions, grouped by challenge question. Excel uses one worksheet per question;
          PDF starts each question on a separate page.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={() => { exportReport('pdf', room, entries); onClose(); }}>PDF</Button>
        <Button variant="contained" onClick={() => { exportReport('excel', room, entries); onClose(); }}>Excel</Button>
      </DialogActions>
    </Dialog>
  );
}

function FeaturedBoard({ featured, room, onClear }) {
  return (
    <Card sx={{ ...SURFACE, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={900} color="primary" sx={{ mb: 1 }}>
          Featured on the board
        </Typography>
        {featured ? (
          <>
            <Typography fontWeight={800}>
              {featured.studentName} {featured.studentId ? `(${featured.studentId})` : ''}
              {' · '}{featured.score}/100
            </Typography>
            <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 1 }}>Original prompt</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', bgcolor: '#f3f8ff', p: 2, borderRadius: 2 }}>
              {featured.originalPrompt || 'Not available'}
            </Typography>
            <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 1 }}>AI-improved prompt</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', bgcolor: '#fff2e6', p: 2, borderRadius: 2 }}>
              {featured.improvedPrompt || 'AI feedback pending'}
            </Typography>
            {room?.isHost && <Button onClick={onClear}>Clear featured prompt</Button>}
          </>
        ) : (
          <Typography color="text.secondary">The challenge host can feature a prompt here.</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function ReviewPanel({ review }) {
  if (!review) return null;
  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: '#f3f8ff', borderRadius: 2 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip color="primary" label={`Submitted prompt quality (AI estimate): ${review.score}/100`} />
        <Chip label={`Language: ${review.language || 'Unknown'}`} />
      </Stack>
      <Typography fontWeight={900} sx={{ mt: 2 }}>How you should have asked (AI suggestion)</Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap', p: 1.5, bgcolor: '#fff', borderRadius: 1 }}>
        {review.improvedPrompt}
      </Typography>
      <Button size="small" startIcon={<ContentCopyIcon />}
        onClick={() => navigator.clipboard?.writeText(review.improvedPrompt || '')}>
        Copy
      </Button>
      <Typography fontWeight={900} sx={{ mt: 2 }}>What changed and why</Typography>
      {(review.changes || []).map((change, index) => (
        <Box key={`${change.label}-${index}`} sx={{ borderLeft: `4px solid ${ORANGE}`, pl: 1.5, mt: 1, bgcolor: '#fff9f3', py: .5 }}>
          <Typography fontWeight={800}>{change.label}</Typography>
          <Typography variant="body2">{change.explanation}</Typography>
        </Box>
      ))}
      <Typography sx={{ mt: 1 }}>{review.nativeExplanation}</Typography>
    </Box>
  );
}

function PromptWorkspace({ room, draft, setDraft, review, setReview, language, setLanguage, listening, speak, busy, submit }) {
  return (
    <Card sx={SURFACE}>
      <CardContent>
        <Typography variant="h6" fontWeight={900} color="primary">Prompt workspace</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Write a prompt that would help an AI answer the challenge question. Submit the original
          prompt first; AI will then suggest improvements without answering the question for you.
        </Typography>
        <TextField
          fullWidth multiline minRows={5} value={draft}
          disabled={room?.status !== 'ACTIVE'}
          placeholder="Type your prompt in English or your native language…"
          inputProps={{ maxLength: 4000 }}
          onChange={(event) => { setDraft(event.target.value); setReview(null); }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <TextField select size="small" label="Voice language" value={language}
            onChange={(event) => setLanguage(event.target.value)} sx={{ minWidth: 180 }}>
            {LANGUAGES.map(([code, label]) => <MenuItem key={code} value={code}>{label}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={listening ? <StopIcon /> : <MicIcon />} onClick={speak}>
            {listening ? 'Stop voice' : 'Speak'}
          </Button>
          <Button variant="contained" sx={{ bgcolor: ORANGE }} startIcon={<AutoAwesomeIcon />}
            disabled={busy || !draft.trim() || room?.status !== 'ACTIVE'} onClick={submit}>
            {busy ? 'Submitting / reviewing…' : 'Submit prompt & get AI feedback'}
          </Button>
        </Stack>
        <ReviewPanel review={review} />
      </CardContent>
    </Card>
  );
}

function TopPrompts({ ranked, room, feature }) {
  return (
    <Card sx={{ ...SURFACE, position: { md: 'sticky' }, top: 16 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={900} color="primary">Top 3 student prompts</Typography>
        <Typography variant="caption" color="text.secondary">
          Live updates every 4 seconds. AI rankings are suggestions; the host chooses what to feature.
        </Typography>
        <Box sx={{ maxHeight: '72vh', overflowY: 'auto', mt: 1 }}>
          {ranked.slice(0, 3).map((entry, index) => (
            <Box key={entry.id} sx={{ p: 1.5, mb: 1, bgcolor: index % 2 ? '#f5f9ff' : '#fff9f4', border: '1px solid #d8e3f2', borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={800}>
                  {entry.studentName} {entry.studentId ? `(${entry.studentId})` : ''}
                </Typography>
                <Chip size="small" label={`${entry.score}/100`} />
              </Stack>
              <Chip size="small" color="success" label={`AI Top ${index + 1}`} sx={{ mt: .5 }} />
              <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 800 }}>Original prompt</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.originalPrompt}</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 800 }}>Improved prompt</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.improvedPrompt}</Typography>
              {room?.isHost && <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => feature(entry.id)}>Feature on board</Button>}
            </Box>
          ))}
          {!ranked.length && <Typography color="text.secondary">Waiting for student prompts…</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// Match words independently, regardless of their order or punctuation.
// Examples: "KW AID 12", "12-BCME", and "BCME 12".
function matchesStudentSearch(entry, query) {
  const normalize = (value) => String(value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const raw = `${entry.studentName || ''} ${entry.studentId || ''}`;
  const haystack = normalize(raw);
  const compact = haystack.replace(/\s+/g, '');
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  return terms.every((term) => haystack.includes(term) || compact.includes(term));
}

function PromptCards({ entries, setDetail }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => [...entries]
    .filter((entry) => matchesStudentSearch(entry, search))
    .sort((a, b) => Number(b.score) - Number(a.score) || Number(b.id) - Number(a.id)),
  [entries, search]);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Prompt submissions</Typography>
        <TextField
          size="small"
          label="Search student name or ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: { xs: 180, sm: 300 } }}
        />
      </Stack>
      <Grid container spacing={1.5}>
        {filtered.map((entry) => (
          <Grid item xs={12} sm={6} md={4} key={entry.id}>
            <Card sx={{ ...SURFACE, cursor: 'pointer', p: 2 }} onClick={() => setDetail(entry)}>
              <Typography fontWeight={800}>
                {entry.studentName} {entry.studentId ? `(${entry.studentId})` : ''}
              </Typography>
              <Chip size="small" label={`${entry.score || 0}/100`} />
              <Typography variant="body2" sx={{ mt: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {entry.originalPrompt}
              </Typography>
              <Button size="small">Expand</Button>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function PromptStudio() {
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [room, setRoom] = useState(null);
  const [entries, setEntries] = useState([]);
  const [title, setTitle] = useState('Prompt Engineering Challenge');
  const [newQuestion, setNewQuestion] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [draft, setDraft] = useState('');
  const [review, setReview] = useState(null);
  const [language, setLanguage] = useState('en-IN');
  const [tab, setTab] = useState(0);
  const [roomTab, setRoomTab] = useState(0);
  const [context, setContext] = useState({ canHost: false, classes: [] });
  const [classId, setClassId] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [listening, setListening] = useState(false);
  const recognition = useRef(null);

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await api.get('/rooms');
      setRooms((data.rooms || []).filter((item) => item.Status === 'ACTIVE'));
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }, []);

  // Search remotely, like Live Classroom. Do not filter results a second time
  // using MUI's default substring matcher (which rejects comma-separated terms).
  useEffect(() => {
    if (!context.canHost) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingClasses(true);
      try {
        const { data } = await api.get('/classes', { params: { search: classSearch } });
        if (!cancelled) setClassOptions(Array.isArray(data?.classes) ? data.classes : []);
      } catch (requestError) {
        if (!cancelled) setError(errorMessage(requestError));
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [classSearch, context.canHost]);

  const refresh = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/rooms/${id}`);
      setRoom(data.room);
      setEntries(data.entries || []);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }, []);

  const openRoom = useCallback((id) => {
    setRoomId(String(id));
    setRoomTab(0);
    setDraft('');
    setReview(null);
    setError('');
  }, []);

  useEffect(() => {
    loadRooms();
    api.get('/context').then(({ data }) => setContext(data)).catch((requestError) => setError(errorMessage(requestError)));
    const code = new URLSearchParams(window.location.search).get('promptCode');
    if (code) {
      setJoinCode(code.toUpperCase().slice(0, 6));
      api.get(`/join/${encodeURIComponent(code)}`)
        .then(({ data }) => openRoom(data.room.RoomID))
        .catch(() => setTab(2));
    }
  }, [loadRooms, openRoom]);

  useEffect(() => {
    if (!roomId) return undefined;
    refresh(roomId);
    const timer = setInterval(() => refresh(roomId), 4000);
    return () => clearInterval(timer);
  }, [roomId, refresh]);

  useEffect(() => () => recognition.current?.stop(), []);

  const act = async (operation) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await operation();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const create = () => act(async () => {
    const { data } = await api.post('/rooms', { title, question: newQuestion, classId: classId || null });
    openRoom(data.roomId);
    setNotice(`Challenge created. Join code: ${data.joinCode}`);
    await loadRooms();
  });

  const join = () => act(async () => {
    const { data } = await api.get(`/join/${joinCode.trim().toUpperCase()}`);
    openRoom(data.room.RoomID);
  });

  const updateQuestion = () => act(async () => {
    await api.put(`/rooms/${roomId}/question`, { question: newQuestion });
    await refresh(roomId);
    setNotice('Question updated.');
  });

  const submit = () => act(async () => {
    const { data } = await api.post(`/rooms/${roomId}/submissions`, { originalPrompt: draft });
    setReview(data.review || null);
    setDraft('');
    await refresh(roomId);
    setNotice(data.reviewError || 'Prompt submitted! AI suggestions are shown below.');
  });

  const feature = (id) => act(async () => {
    await api.put(`/rooms/${roomId}/feature`, { submissionId: id });
    await refresh(roomId);
  });

  const close = () => {
    if (!window.confirm('End this challenge for everyone?')) return;
    act(async () => {
      await api.put(`/rooms/${roomId}/close`);
      await refresh(roomId);
      setNotice('Challenge ended for all participants.');
    });
  };

  const speak = () => {
    if (listening) {
      recognition.current?.stop();
      return;
    }
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Speech) {
      setError('Voice transcription is not supported in this browser. Please type instead.');
      return;
    }
    const instance = new Speech();
    recognition.current = instance;
    instance.lang = language;
    instance.continuous = true;
    instance.interimResults = false;
    instance.onresult = (event) => {
      const text = Array.from(event.results).slice(event.resultIndex)
        .filter((result) => result.isFinal).map((result) => result[0].transcript).join(' ');
      setDraft((previous) => `${previous} ${text}`.trim());
    };
    instance.onerror = (event) => setError(`Voice recognition: ${event.error}`);
    instance.onend = () => setListening(false);
    try {
      instance.start();
      setListening(true);
    } catch (speechError) {
      setError(speechError.message);
    }
  };

  const ranked = useMemo(
    () => [...entries].sort((a, b) => Number(b.score) - Number(a.score) || Number(b.id) - Number(a.id)),
    [entries],
  );
  const featured = room?.featuredPrompt || entries.find((entry) => String(entry.id) === String(room?.featuredId));
  const isStudentOnly = !context.canHost;
  const visibleEntries = isStudentOnly ? [] : entries;
  const joinUrl = room?.joinCode
    ? `${window.location.origin}/PromptStudio?promptCode=${encodeURIComponent(room.joinCode)}`
    : '';

  return (
    <Box sx={{ p: 2, bgcolor: '#F2F2F2', minHeight: '100vh' }}>
      <Card sx={{ ...SURFACE, overflow: 'hidden', boxShadow: '0 3px 10px #0002' }}>
        <Box sx={{ bgcolor: ORANGE, color: '#fff', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={900}>Prompt Studio</Typography>
            <Typography variant="body2">Role-led AI prompt engineering challenges · Voice and multilingual input · Visible improvements</Typography>
            {room && <Typography variant="body2">{room.title} · Code: {room.joinCode} · {room.status}</Typography>}
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            {joinUrl && (
              <Tooltip title="Expand join QR code">
                <Box onClick={() => setQrOpen(true)} sx={{ p: .5, bgcolor: '#fff', borderRadius: 1, cursor: 'pointer' }}>
                  <QRCodeSVG size={48} value={joinUrl} />
                </Box>
              </Tooltip>
            )}
            {room?.isHost && (
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => setReportOpen(true)}
                sx={{ bgcolor: '#fff', color: '#b84c00', fontWeight: 800, '&:hover': { bgcolor: '#fff3e8' } }}
              >
                Download Class Report
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton sx={{ color: '#fff' }} onClick={() => roomId ? refresh(roomId) : loadRooms()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <CardContent>
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
          {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

          {!roomId ? (
            <Paper elevation={0} sx={{ ...SURFACE, overflow: 'hidden' }}>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto"
                sx={{ px: 1, borderBottom: '1px solid #d4e2f4', '& .MuiTab-root': { textTransform: 'none', fontWeight: 800 }, '& .Mui-selected': { color: `${ORANGE} !important` }, '& .MuiTabs-indicator': { backgroundColor: ORANGE } }}>
                <Tab label="Prompt Challenges" />
                <Tab label="Start Prompt Challenge" disabled={!context.canHost} />
                <Tab label="Join With Code" />
              </Tabs>
              <Box sx={{ p: 2 }}>
                {tab === 0 && (
                  <>
                    <Typography fontWeight={900} color="primary" sx={{ mb: 1 }}>Available prompt challenges</Typography>
                    {rooms.length ? rooms.map((item) => (
                      <Card key={item.RoomID} sx={{ ...SURFACE, mb: 1, p: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1}>
                          <Box>
                            <Typography fontWeight={800}>{item.Title}</Typography>
                            <Typography variant="body2">
                              {item.Question} · {item.Status} {item.ClassID ? `· Class ${item.ClassID}` : ''}
                            </Typography>
                          </Box>
                          <Button variant="outlined" onClick={() => openRoom(item.RoomID)}>Join challenge</Button>
                        </Stack>
                      </Card>
                    )) : <Typography color="text.secondary">No challenges found. Ask your teacher to create one, or join with a code.</Typography>}
                  </>
                )}
                {tab === 1 && (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="h6" fontWeight={900} color="primary">
                      Create a prompt challenge
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Challenge title"
                          value={title}
                          onChange={(event) => setTitle(event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={classOptions}
                          value={classOptions.find((item) => String(item.ClassID) === String(classId)) || null}
                          loading={loadingClasses}
                          inputValue={classSearch}
                          onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') setClassSearch(value); }}
                          filterOptions={(options) => options}
                          onChange={(_, selected) => { setClassId(selected ? String(selected.ClassID) : ''); if (selected) setClassSearch([selected.SubCampusCode, selected.BranchCode, `Sem ${selected.SemesterCode}`, `Section ${selected.Section}`, selected.Regulation, selected.SubjectNameCode || selected.ClassName].filter(Boolean).join(' · ')); }}
                          getOptionLabel={(item) => [
                            item.SubCampusCode, item.BranchCode,
                            item.SemesterCode || item.Semester, item.Section, item.Regulation,
                            item.SubjectNameCode || item.ClassName,
                          ].filter(Boolean).join(' · ') || `Class ${item.ClassID}`}
                          isOptionEqualToValue={(option, value) => String(option.ClassID) === String(value.ClassID)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search class (optional)"
                              placeholder="Example: KW,12,D,12-BCME"
                              helperText="Optional — search using comma-separated fields in any order, like Live Classroom."
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth multiline minRows={4}
                          label="Question for students"
                          value={newQuestion}
                          onChange={(event) => setNewQuestion(event.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Button
                      sx={{ mt: 2, bgcolor: ORANGE }} variant="contained"
                      disabled={busy || !title.trim() || !newQuestion.trim()}
                      onClick={create}
                    >
                      Create challenge
                    </Button>
                  </Box>
                )}
                {tab === 2 && (
                  <Box>
                    <Typography variant="h6" fontWeight={900} color="primary">Join a prompt challenge</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, maxWidth: 440 }}>
                      <TextField size="small" label="Join code" value={joinCode}
                        onChange={(event) => setJoinCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                        inputProps={{ maxLength: 6 }} />
                      <Button variant="outlined" disabled={busy || joinCode.length !== 6} onClick={join}>Join</Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      You can also scan the challenge QR code with your phone.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          ) : (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                  <Typography variant="h6" fontWeight={900} color="primary">{room?.title || 'Loading…'}</Typography>
                  <Typography variant="body2">Join code: <b>{room?.joinCode}</b> · {room?.status}</Typography>
                </Box>
                <Button variant="outlined" onClick={() => { setRoomId(''); setRoom(null); loadRooms(); }}>
                  Back to rooms
                </Button>
              </Stack>

              <Box sx={{ ...SURFACE, p: 2, bgcolor: '#fff2e6', my: 2 }}>
                <Typography variant="subtitle2" fontWeight={900}>CHALLENGE QUESTION</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 18 }}>{room?.question}</Typography>
                {room?.isHost && room?.status === 'ACTIVE' && (
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <TextField fullWidth multiline minRows={2} label="Ask a new question" value={newQuestion}
                      onChange={(event) => setNewQuestion(event.target.value)} />
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" disabled={busy || !newQuestion.trim()} onClick={updateQuestion}>Post new question</Button>
                      <Button color="error" variant="outlined" disabled={busy} onClick={close}>End challenge</Button>
                    </Stack>
                  </Stack>
                )}
              </Box>

              <Tabs value={roomTab} onChange={(_, value) => setRoomTab(value)} sx={{ mb: 2 }}>
                <Tab label="Workspace" />
                {!isStudentOnly && <Tab label={`Prompt cards (${visibleEntries.length})`} />}
              </Tabs>

              {roomTab === 0 || isStudentOnly ? (
                <>
                  <FeaturedBoard featured={featured} room={room} onClear={() => feature(null)} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={isStudentOnly ? 12 : 8}>
                      <PromptWorkspace
                        room={room} draft={draft} setDraft={setDraft}
                        review={review} setReview={setReview}
                        language={language} setLanguage={setLanguage}
                        listening={listening} speak={speak}
                        busy={busy} submit={submit}
                      />
                    </Grid>
                    {!isStudentOnly && (
                      <Grid item xs={12} md={4}>
                        <TopPrompts ranked={ranked} room={room} feature={feature} />
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <PromptCards entries={visibleEntries} setDetail={setDetail} />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, width: 366, maxWidth: 'calc(100vw - 32px)', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ textAlign: 'center', px: 2.5, pt: 3, pb: 2.5 }}>
          <Typography fontWeight={900} color="#1565C0" sx={{ mb: 2 }}>
            Scan to Join Prompt Challenge
          </Typography>
          {joinUrl && <QRCodeSVG size={320} value={joinUrl} style={{ width: '100%', maxWidth: 320, height: 'auto' }} />}
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5 }}>
            Click outside this popup to close
          </Typography>
        </DialogContent>
      </Dialog>

      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        room={room}
        entries={entries}
      />

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.studentName} {detail?.studentId ? `(${detail.studentId})` : ''} · {detail?.score}/100
        </DialogTitle>
        <DialogContent dividers>
          <Typography fontWeight={800}>Original prompt</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{detail?.originalPrompt}</Typography>
          <Typography fontWeight={800}>AI suggested prompt</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{detail?.improvedPrompt}</Typography>
          <Typography>{detail?.review?.nativeExplanation}</Typography>
        </DialogContent>
        <DialogActions>
          {room?.isHost && <Button onClick={() => { feature(detail.id); setDetail(null); }}>Feature</Button>}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
